/**
 * OpenRouter API Service
 *
 * Provides access to OpenRouter model discovery and usage tracking.
 * Uses the shared auth headers for API key resolution.
 */

import { getAuthHeaders } from './authConfig';

const API_BASE = '/api/openrouter';

/**
 * Get headers with auth context
 */
function getHeaders() {
  return getAuthHeaders();
}

/**
 * List available models from OpenRouter
 *
 * Returns models with pricing, context length, and capabilities.
 *
 * @returns {Promise<{models: Array<{id: string, name: string, description: string, context_length: number, pricing: object, supports_tools: boolean, supports_reasoning: boolean, provider: string}>}>}
 */
export async function listModels() {
  const response = await fetch(`${API_BASE}/models`, {
    headers: getHeaders(),
    credentials: 'include',
  });
  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('OpenRouter API key not configured');
    }
    throw new Error(`Failed to fetch models: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Get usage and remaining credits for the current API key
 *
 * @returns {Promise<{limit: number|null, limit_remaining: number|null, limit_reset: string|null, usage: number, usage_daily: number, usage_weekly: number, usage_monthly: number, is_free_tier: boolean}>}
 */
export async function getUsage() {
  const response = await fetch(`${API_BASE}/usage`, {
    headers: getHeaders(),
    credentials: 'include',
  });
  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('OpenRouter API key not configured');
    }
    throw new Error(`Failed to fetch usage: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Get details for a specific generation (tokens, cost)
 *
 * @param {string} generationId - The generation ID from a completion response
 * @returns {Promise<object>} Generation details with tokens and cost
 */
export async function getGeneration(generationId) {
  const response = await fetch(`${API_BASE}/generation/${generationId}`, {
    headers: getHeaders(),
    credentials: 'include',
  });
  if (!response.ok) {
    if (response.status === 401) {
      throw new Error('OpenRouter API key not configured');
    }
    throw new Error(`Failed to fetch generation: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Test OpenRouter API connection
 *
 * @returns {Promise<{status: string, message: string}>}
 */
export async function testConnection() {
  const response = await fetch(`${API_BASE}/test`, {
    headers: getHeaders(),
    credentials: 'include',
  });
  if (!response.ok) {
    if (response.status === 401) {
      const error = await response.json().catch(() => ({ detail: 'Invalid API key' }));
      throw new Error(error.detail || 'Invalid API key');
    }
    throw new Error(`Connection test failed: ${response.statusText}`);
  }
  return response.json();
}

/**
 * Format pricing for display
 *
 * @param {object} pricing - Pricing object from OpenRouter
 * @returns {string} Formatted pricing string
 */
export function formatPricing(pricing) {
  if (!pricing) return 'N/A';

  const prompt = parseFloat(pricing.prompt) || 0;
  const completion = parseFloat(pricing.completion) || 0;

  // OpenRouter prices are per token, we show per 1M tokens
  const promptPer1M = (prompt * 1000000).toFixed(2);
  const completionPer1M = (completion * 1000000).toFixed(2);

  return `$${promptPer1M} / $${completionPer1M} per 1M tokens`;
}

/**
 * Format context length for display
 *
 * @param {number} contextLength - Context length in tokens
 * @returns {string} Formatted context length
 */
export function formatContextLength(contextLength) {
  if (!contextLength) return 'N/A';

  if (contextLength >= 1000000) {
    return `${(contextLength / 1000000).toFixed(1)}M`;
  }
  if (contextLength >= 1000) {
    return `${Math.round(contextLength / 1000)}K`;
  }
  return contextLength.toString();
}

/**
 * Format usage amount for display
 *
 * @param {number} usage - Usage amount in dollars
 * @returns {string} Formatted usage string
 */
export function formatUsage(usage) {
  if (usage === null || usage === undefined) return '$0.00';
  return `$${parseFloat(usage).toFixed(2)}`;
}

/**
 * Filter models by capabilities
 *
 * @param {Array} models - List of models
 * @param {object} filters - Filter options
 * @param {boolean} filters.supportsTools - Filter for tool support
 * @param {boolean} filters.supportsReasoning - Filter for reasoning support
 * @param {string} filters.provider - Filter by provider name
 * @returns {Array} Filtered models
 */
export function filterModels(models, filters = {}) {
  return models.filter(model => {
    if (filters.supportsTools && !model.supports_tools) return false;
    if (filters.supportsReasoning && !model.supports_reasoning) return false;
    if (filters.provider && model.provider !== filters.provider) return false;
    return true;
  });
}

/**
 * Group models by provider
 *
 * @param {Array} models - List of models
 * @returns {Object} Models grouped by provider
 */
export function groupModelsByProvider(models) {
  return models.reduce((acc, model) => {
    const provider = model.provider || 'unknown';
    if (!acc[provider]) {
      acc[provider] = [];
    }
    acc[provider].push(model);
    return acc;
  }, {});
}
